'use client'

import { Link } from 'react-router-dom'
import { Heart, ShoppingCart, Trash2, Share2, ShoppingBag } from 'lucide-react'
import { useWishlistStore } from '@/stores/wishlist'
import { useCartStore } from '@/stores/cart'
import { useCurrencyStore } from '@/stores/currency'
import { useProducts } from '@/hooks/useProducts'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import ECommerceHeader from '@/components/layout/ECommerceHeader'
import Footer from '@/components/layout/Footer'
import type { Product } from '@/types/product'

export default function WishlistPage() {
  const { items: wishlistIds, removeItem } = useWishlistStore()
  const addToCart = useCartStore((state) => state.addItem)
  const getSymbol = useCurrencyStore((state) => state.getSymbol)
  const convert = useCurrencyStore((state) => state.convert)

  // Fetch all products to find wishlist items
  const { data, isLoading } = useProducts({ limit: 500 })
  const allProducts = data?.products || []

  // Filter wishlist products
  const wishlistProducts = allProducts.filter((p) => wishlistIds.includes(p.id))

  const handleAddToCart = (product: Product) => {
    addToCart({
      product_id: product.id,
      title: product.title,
      image: product.images[0]?.url || '',
      price: product.pricing.current_price,
      original_price: product.pricing.original_price,
      vendor_name: product.vendor.name,
    })
  }

  const handleShare = async () => {
    const wishlistUrl = window.location.href
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'My BotTola Wishlist',
          text: `Check out my wishlist with ${wishlistProducts.length} items!`,
          url: wishlistUrl,
        })
      } catch (err) {
        // User cancelled
      }
    } else {
      navigator.clipboard.writeText(wishlistUrl)
      alert('Wishlist link copied to clipboard!')
    }
  }

  return (
    <div className="min-h-screen bg-background">
      <ECommerceHeader />

      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground">My Wishlist</h1>
            <p className="text-muted-foreground mt-1">
              {wishlistProducts.length} {wishlistProducts.length === 1 ? 'item' : 'items'}
            </p>
          </div>
          {wishlistProducts.length > 0 && (
            <Button variant="outline" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-2" />
              Share Wishlist
            </Button>
          )}
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="p-4">
                <div className="flex gap-4">
                  <Skeleton className="w-24 h-24 rounded-md" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-5 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                    <Skeleton className="h-6 w-1/3" />
                  </div>
                </div>
              </Card>
            ))}
          </div>
        ) : wishlistProducts.length === 0 ? (
          <div className="text-center py-16">
            <Heart className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-foreground mb-2">Your wishlist is empty</h2>
            <p className="text-muted-foreground mb-6">
              Save items you love by clicking the heart icon on any product
            </p>
            <Link to="/">
              <Button variant="primary">
                <ShoppingBag className="h-4 w-4 mr-2" />
                Start Shopping
              </Button>
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {wishlistProducts.map((product) => (
              <Card key={product.id} className="p-4">
                <div className="flex gap-4">
                  {/* Product Image */}
                  <Link to={`/products/${product.slug}`} className="flex-shrink-0">
                    <div className="relative w-24 h-24 rounded-md overflow-hidden bg-muted">
                      <img
                        src={product.images[0]?.url || ''}
                        alt={product.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform"
                      />
                      {product.pricing.discount_percentage > 0 && (
                        <div className="absolute top-1 left-1 bg-accent text-accent-foreground text-xs px-1.5 py-0.5 rounded">
                          -{product.pricing.discount_percentage}%
                        </div>
                      )}
                    </div>
                  </Link>

                  {/* Product Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/products/${product.slug}`}>
                      <h3 className="font-medium text-foreground line-clamp-2 hover:text-primary transition-colors">
                        {product.title}
                      </h3>
                    </Link>
                    <p className="text-xs text-muted-foreground mt-1">{product.vendor.name}</p>

                    {/* Price */}
                    <div className="flex items-baseline gap-2 mt-2">
                      <span className="text-lg font-bold text-brand-orange">
                        {getSymbol()}
                        {convert(product.pricing.current_price).toLocaleString()}
                      </span>
                      {product.pricing.discount_percentage > 0 && (
                        <span className="text-sm text-muted-foreground line-through">
                          {getSymbol()}
                          {convert(product.pricing.original_price).toLocaleString()}
                        </span>
                      )}
                    </div>

                    {/* Stock Status */}
                    <p
                      className={`text-xs mt-1 ${
                        product.stock_status === 'in_stock'
                          ? 'text-success'
                          : product.stock_status === 'low_stock'
                            ? 'text-orange-500'
                            : 'text-destructive'
                      }`}
                    >
                      {product.stock_status === 'in_stock'
                        ? 'In Stock'
                        : product.stock_status === 'low_stock'
                          ? 'Low Stock'
                          : 'Out of Stock'}
                    </p>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-2 mt-4">
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-1"
                    onClick={() => handleAddToCart(product)}
                    disabled={product.stock_status === 'out_of_stock'}
                  >
                    <ShoppingCart className="h-4 w-4 mr-1" />
                    Add to Cart
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => removeItem(product.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>

      <Footer />
    </div>
  )
}
